<template>
  <RevForm
    class="mt-56 flex-wrap items-start gap-12 md:flex"
    :initial-values
    :name="FORM_ID"
    :validate
    @submit="handleSubmit"
  >
    <template #default="{ values, errors, hasError }">
      <div class="min-w-[260px]">
        <RevInputFileUpload
          id="document_upload_input"
          v-model="values.files"
          :accept="ALLOWED_FILES_EXTENSIONS"
          :description="getInputDescription(errors)"
          :errors="getFileErrors(errors)"
          :label-empty="i18n(translations.fileInputLabel)"
          :label-not-empty="i18n(translations.fileInputLabel)"
          :max-file-size="ALLOWED_FILES_SIZE"
          :max-files="ALLOWED_FILES_COUNT"
          @click="handleInputFileClick"
          @errors="handleInputFileErrors"
        />
      </div>
      <RevInputSelect
        id="document_type_select"
        v-model="values.fileType"
        class="mb-16 mt-12 min-w-[260px] md:mt-0"
        :error="errors.fileType"
        :label="i18n(translations.documentTypeslabel)"
        :options="documentTypeOptions"
      />
      <RevButton
        id="add_contact_button"
        :disabled="hasError"
        :loading="isSubmitting"
        type="submit"
        variant="primary"
      >
        {{ i18n(translations.submitButton) }}
      </RevButton>
    </template>
  </RevForm>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue'

import type { GetLegalDocumentsMetadata } from '@backmarket/http-api/src/api-specs-seller-backoffice/seller-profile'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import {
  RevForm,
  makeValidate,
  required,
  setFormValues,
} from '@ds/components/Form'
import { RevInputFileUpload } from '@ds/components/InputFileUpload'
import type { FileError } from '@ds/components/InputFileUpload/InputFileUpload.type'
import { RevInputSelect } from '@ds/components/InputSelect'

import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import { FORM_IDS } from '../../../../../../Profile.constants'
import { uploadLegalDocument } from '../../../../../../Profile.utils'
import {
  ALLOWED_FILES_COUNT,
  ALLOWED_FILES_EXTENSIONS,
  ALLOWED_FILES_SIZE,
  ERROR_TYPES,
} from '../../../../LegalDetails.constants'

import translations from './LegalDocumentsForm.translations'
import type {
  ExtendedFileError,
  FormLegalDocumentsErrors,
  FormLegalDocumentsValues,
} from './LegalDocumentsForm.types'

const FORM_ID = FORM_IDS.EDIT_LEGAL_DOCUMENTS

const props = defineProps<{
  documentTypes: GetLegalDocumentsMetadata['legalDocumentTypes']
}>()

const emit = defineEmits(['legal-document-uploaded'])

const i18n = useI18n()
const { id: sellerId } = useSellerInfoStore()
const { openSuccessToast } = useTheToast()

const cancelUploadFilePoll = ref<() => void>()

onUnmounted(() => {
  if (cancelUploadFilePoll.value) {
    cancelUploadFilePoll.value()
  }
})

const FILE_ERRORS = {
  [ERROR_TYPES.MAX_FILE_SIZE_EXCEEDED]: i18n(
    translations.fileInputErrorMaxSize,
  ),
  [ERROR_TYPES.WRONG_FILE_EXTENSION]: i18n(translations.fileInputErrorMimeType),
}

const initialValues: FormLegalDocumentsValues = { files: [], fileType: '' }

const isSubmitting = ref(false)
const fileErrors = ref<ExtendedFileError[]>([])

const documentTypeOptions = computed(() =>
  props.documentTypes.map(({ label, code }) => ({
    label: label || '',
    value: code,
  })),
)

function clearErrors() {
  fileErrors.value = []
}

function getInputDescription(errors: FormLegalDocumentsErrors) {
  if (fileErrors.value.length) {
    return fileErrors.value[0].label
  }

  return errors.files || i18n(translations.fileInputDescription)
}

function getFileErrors(errors: FormLegalDocumentsErrors) {
  if (fileErrors.value.length) {
    return fileErrors.value
  }

  return errors.files
    ? [{ label: errors.files } as unknown as FileError]
    : undefined
}

function handleInputFileClick() {
  clearErrors()
}

function handleInputFileErrors(errors: FileError[]) {
  fileErrors.value = errors.map((error) => ({
    ...error,
    label: FILE_ERRORS[error.type],
  }))
}

const validate = makeValidate<FormLegalDocumentsValues>({
  fileType: [required(i18n(translations.requiredField))],
  files: [required(i18n(translations.requiredField))],
})

async function handleSubmit({ files, fileType }: FormLegalDocumentsValues) {
  isSubmitting.value = true
  try {
    if (sellerId && fileType) {
      await uploadLegalDocument(
        sellerId,
        files[0],
        fileType,
        cancelUploadFilePoll,
      )
      emit('legal-document-uploaded')
      openSuccessToast({ title: i18n(translations.toastAddSuccessContent) })
      setFormValues(FORM_ID, initialValues)
    }
  } catch {
    // Fails silently
  } finally {
    isSubmitting.value = false
    clearErrors()
  }
}
</script>
